import { TEXTOS } from '../../lib/glossario'
import type { ConteudoDoResumo } from '../../types/repositorios'
import { rotuloDoSetor } from './setores'

const CLASSE_DA_SEVERIDADE: Record<string, string> = {
  ALTA: 'bg-red-100 text-red-900',
  MEDIA: 'bg-amber-100 text-amber-900',
  BAIXA: 'bg-slate-100 text-slate-700',
}

/**
 * O corpo do resumo: setores, pessoas, pontos de atenção e perguntas.
 *
 * Os pontos de atenção usam o mesmo desenho das ressalvas do parecer de GMUD — título, o que os
 * dados mostram, como conferir — para quem já lê um reconhecer o outro. A severidade aparece
 * escrita, e não só em cor: é o único dado que separa "a conferir" de "olhar hoje".
 *
 * @param props o conteúdo estruturado que o modelo escreveu
 * @returns as seções do resumo
 */
export function CorpoDoResumo({ conteudo }: { conteudo: ConteudoDoResumo }) {
  const t = TEXTOS.repositorios.resumo
  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-slate-800">{conteudo.visaoGeral}</p>

      {conteudo.pontosDeAtencao.length > 0 && (
        <section aria-label={t.pontosDeAtencao}>
          <h3 className="text-sm font-semibold text-slate-800">{t.pontosDeAtencao}</h3>
          <ul className="mt-2 space-y-2">
            {conteudo.pontosDeAtencao.map((ponto, indice) => (
              <li key={`${ponto.codigo}-${indice}`} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-800">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${CLASSE_DA_SEVERIDADE[ponto.severidade] ?? CLASSE_DA_SEVERIDADE.MEDIA}`}
                  >
                    {t.severidade[ponto.severidade] ?? ponto.severidade}
                  </span>
                  {ponto.titulo ?? ponto.codigo}
                </p>
                {ponto.explicacao && <p className="mt-1 text-sm text-slate-600">{ponto.explicacao}</p>}
                {ponto.verificacao && (
                  <p className="mt-1 text-sm text-slate-500">
                    <span className="font-medium">{t.comoConferir}</span> {ponto.verificacao}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {conteudo.porSetor.length > 0 && (
        <section aria-label={t.porSetor}>
          <h3 className="text-sm font-semibold text-slate-800">{t.porSetor}</h3>
          <dl className="mt-2 space-y-2">
            {conteudo.porSetor.map((trecho) => (
              <div key={trecho.setor}>
                <dt className="text-sm font-medium text-slate-700">{rotuloDoSetor(trecho.setor)}</dt>
                <dd className="text-sm text-slate-600">{trecho.resumo}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {conteudo.porPessoa.length > 0 && (
        <section aria-label={t.porPessoa}>
          <h3 className="text-sm font-semibold text-slate-800">{t.porPessoa}</h3>
          <dl className="mt-2 grid gap-2 md:grid-cols-2">
            {conteudo.porPessoa.map((trecho) => (
              <div key={`${trecho.nome}-${trecho.setor}`} className="rounded-md border border-slate-200 p-3">
                <dt className="text-sm font-medium text-slate-800">
                  {trecho.nome} <span className="font-normal text-slate-500">· {rotuloDoSetor(trecho.setor)}</span>
                </dt>
                <dd className="mt-0.5 text-sm text-slate-600">{trecho.resumo}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {conteudo.perguntasParaOFiscal.length > 0 && (
        <section aria-label={t.perguntas}>
          <h3 className="text-sm font-semibold text-slate-800">{t.perguntas}</h3>
          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-700">
            {conteudo.perguntasParaOFiscal.map((pergunta) => (
              <li key={pergunta}>{pergunta}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
